import {
  DEFAULT_FRIEND_LINK_USES,
  DEFAULT_GROUP_LINK_USES,
  DEFAULT_INVITE_TTL_DAYS,
  DEFAULT_SESSION_LINK_USES,
  MAX_FRIEND_LINK_USES,
  MAX_GROUP_LINK_USES,
  MAX_SESSION_LINK_USES,
} from "@proef/domain";
import { z } from "zod";
import { PublicUser } from "./friendship.js";

export const InviteKind = z.enum(["friend", "group", "session"]);
export type InviteKind = z.infer<typeof InviteKind>;

export const InviteTtlDays = z.union([z.literal(1), z.literal(7), z.literal(30)]);
export type InviteTtlDays = z.infer<typeof InviteTtlDays>;

/** POST /v1/invites -- a personal friend link, a link into a group the caller may invite to, or a
 * link to a session the caller is at. A session link takes no lifetime: it ends with the session. */
export const CreateInviteRequest = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("friend"),
    maxUses: z.int().min(1).max(MAX_FRIEND_LINK_USES).default(DEFAULT_FRIEND_LINK_USES),
    ttlDays: InviteTtlDays.default(DEFAULT_INVITE_TTL_DAYS),
  }),
  z.object({
    kind: z.literal("group"),
    groupId: z.string().min(1),
    maxUses: z.int().min(1).max(MAX_GROUP_LINK_USES).default(DEFAULT_GROUP_LINK_USES),
    ttlDays: InviteTtlDays.default(DEFAULT_INVITE_TTL_DAYS),
    /** Absent means the group's default: on for members-only groups. */
    requireApproval: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal("session"),
    sessionId: z.string().min(1),
    maxUses: z.int().min(1).max(MAX_SESSION_LINK_USES).default(DEFAULT_SESSION_LINK_USES),
  }),
]);
export type CreateInviteRequest = z.infer<typeof CreateInviteRequest>;

export const InviteState = z.enum(["active", "revoked", "expired", "used_up"]);
export type InviteState = z.infer<typeof InviteState>;

export const InviteRedemptionView = z.object({
  user: PublicUser,
  redeemedAt: z.iso.datetime(),
});
export type InviteRedemptionView = z.infer<typeof InviteRedemptionView>;

/** One of the caller's own links. The token is never in it: the edge keeps only its hash. */
export const InviteView = z.object({
  id: z.string().min(1),
  kind: InviteKind,
  groupId: z.string().nullable(),
  groupName: z.string().nullable(),
  /** The session a session link opens; defaults to null for answers from before session links. */
  sessionId: z.string().nullable().default(null),
  sessionName: z.string().nullable().default(null),
  maxUses: z.int().positive(),
  uses: z.int().nonnegative(),
  requireApproval: z.boolean(),
  state: InviteState,
  /** The spoken three-word code (groups and sessions), null once it lapsed or the link was revoked. */
  code: z.string().nullable(),
  codeExpiresAt: z.iso.datetime().nullable(),
  expiresAt: z.iso.datetime(),
  revokedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  redemptions: z.array(InviteRedemptionView),
});
export type InviteView = z.infer<typeof InviteView>;

/** POST /v1/invites answers with the token this once; it goes into the link's URL fragment. */
export const CreatedInvite = InviteView.extend({
  token: z.string().min(22).max(64),
});
export type CreatedInvite = z.infer<typeof CreatedInvite>;

/** GET /v1/invites?mine=1 */
export const InviteList = z.object({ items: z.array(InviteView) });
export type InviteList = z.infer<typeof InviteList>;

/** What an invitee holds: the token from a link, or a code someone said out loud. It travels in a
 * POST body so it stays out of URLs and request logs. */
export const InviteSecret = z.union([
  z.object({ token: z.string().min(16).max(64) }),
  z.object({ code: z.string().min(1).max(80) }),
]);
export type InviteSecret = z.infer<typeof InviteSecret>;

/** POST /v1/invites/preview -- what the landing shows before anyone signs in. Never a member list;
 * a members-only group's name stays hidden. */
export const InvitePreview = z.object({
  kind: InviteKind,
  inviter: PublicUser,
  group: z.object({ name: z.string(), memberCount: z.int().nonnegative() }).nullable(),
  /** What a session link opens: its name and when it starts, never who else is in it. */
  session: z
    .object({ name: z.string(), scheduledAt: z.iso.datetime().nullable() })
    .nullable()
    .default(null),
  requireApproval: z.boolean(),
  expiresAt: z.iso.datetime(),
});
export type InvitePreview = z.infer<typeof InvitePreview>;

export const InviteOutcome = z.enum(["friends", "requested", "joined", "join_requested"]);
export type InviteOutcome = z.infer<typeof InviteOutcome>;

/** POST /v1/invites/accept -- idempotent: accepting again answers with the same outcome. */
export const AcceptInviteResponse = z.object({
  outcome: InviteOutcome,
  kind: InviteKind,
  inviter: PublicUser,
  groupId: z.string().nullable(),
  sessionId: z.string().nullable().default(null),
  /** The flight a session link put the person into; null when the session has none (yet). */
  flightCode: z.string().nullable().default(null),
});
export type AcceptInviteResponse = z.infer<typeof AcceptInviteResponse>;
